/**
 * iPad(가로) 강의실 채팅 — iOS 키보드 닫힘 후 "위 잘림 고착/유령 스크롤" 복구 유틸.
 *
 * 메커니즘: CSSOM 스펙상 overflow:hidden 요소도 스크롤 컨테이너라서, 키보드가 뜰 때
 * 브라우저(UA)가 포커스된 입력(textarea)을 보이게 하려고 조상 컨테이너들
 * (html/#root/#room-container/.body/.layout 등, overflow:hidden 포함)의 scrollTop 을
 * 네이티브로 밀어넣는다(우리 코드의 scrollIntoView 를 제거해도 발생하는 UA 자체 동작).
 * 키보드가 닫혀도 UA 는 이 값을 되돌리지 않아 콘텐츠가 위로 밀린 채 고착된다
 * → 위 잘림 + footer 아래 빈 띠(#000 노출) + 잔존 스크롤 여지.
 *
 * ★ 과거 실패 이력(4e34bb4b·2f5956d8·7c1f580d·757ea3ae·0004910f)과의 구조적 차이:
 * 과거 시도는 전부 #root 의 height/transform/--vh 등 "기하"를 새로 만들어
 * 화면 축소·떨림(jitter)·전체 스크롤 생성 부작용을 냈고 전부 롤백됐다.
 * 본 유틸은 stale 스크롤 오프셋을 0 으로 "원복"만 한다 — 이미 0 이면
 * DOM 쓰기 0회(완전 no-op)라 오발화해도 "원래 레이아웃"으로만 수렴한다.
 * 또한 b62cde3f 의 결함이었던 `if (window.scrollY || ...)` 식 사전 게이트
 * (내부 컨테이너에 stale 이 있으면 전부 0 이라 리셋 전체가 스킵됨)를 금지하고
 * 요소별로 개별 판정한다.
 */
/**
 * iPad 계열 브라우저 판정 — Safari 포함.
 * iPadOS 는 데스크탑형 UA(Macintosh)를 쓰므로 maxTouchPoints 로 구분한다.
 * 리셋은 stale 일 때만 동작하는 자가치유(정상 시 완전 no-op)이므로
 * CriOS|EdgiOS 등 서드파티로 좁히지 않는다. (QA 이상 시에만 폴백으로 축소)
 */
export const isIpadLikeBrowser = () => {
    const ua = window.navigator.userAgent;
    return /iPad/.test(ua) || (/Macintosh/i.test(ua) && (window.navigator.maxTouchPoints || 0) > 1);
};
/**
 * 키보드 "닫힘" 판정 임계(px). diff = innerHeight − visualViewport.height.
 * iPad WKWebView 서드파티 브라우저는 오토필 액세서리 바 79px 를 닫힌 뒤에도
 * 반납하지 않아(실측 vvH 688→609 고착) 폰용 임계 50 을 재사용할 수 없다.
 * diff < 120 이면 닫힘으로 본다. (가로 키보드 열림 diff 실측 493 → 여유 충분)
 */
export const KEYBOARD_CLOSED_DIFF = 120;
/**
 * 단일 요소 스크롤 원복. behavior:'instant' 필수 —
 * reset.scss 의 `html { scroll-behavior: smooth }` 때문에 기본형(2-인자) 스크롤은
 * 애니메이션이 되어 UA 후속 정착에 덮일 수 있다(b62cde3f 결함 중 하나).
 */
const resetElementScroll = (el, resetTop, resetLeft) => {
    try {
        el.scrollTo({
            top: resetTop ? 0 : el.scrollTop,
            left: resetLeft ? 0 : el.scrollLeft,
            behavior: 'instant',
        });
    }
    catch (e) {
        // 구형 브라우저 폴백(옵션 객체 미지원 시)
        if (resetTop)
            el.scrollTop = 0;
        if (resetLeft)
            el.scrollLeft = 0;
    }
};
/**
 * stale scrollTop 스윕.
 * @param start 조상 체인 시작점(채팅 입력 래퍼 `.chat_input` div 또는 textarea ref).
 *              주의: `textarea.chat_input` 셀렉터는 무효 — Input.web.tsx 에서
 *              chat_input 클래스는 래퍼 div 에 붙고 textarea 에는 클래스가 없다.
 *              null/undefined 면(채팅 사이드바 언마운트 후) 고정 목록 경로 사용.
 *
 * 안전 장치:
 * - 각 요소가 scrollTop/scrollLeft 0 이면 continue → 완전 no-op 보장.
 * - html/body 외에는 overflow 가 hidden 인 "축만" 리셋 →
 *   #chat_body_scroll(overflow-y:scroll)·.chat_content(auto) 등 의도적 스크롤러는
 *   체인에 있어도 자동 배제되어 채팅 스크롤 위치가 보존된다.
 * - height/transform/--vh 등 기하는 일절 건드리지 않는다(과거 실패 접근 전면 금지).
 */
export const resetStaleScrollTops = (start) => {
    const targets = new Set();
    if (start) {
        // 포커스 입력에서 documentElement 까지의 조상 전체 —
        // UA reveal 이 scrollTop 을 밀어넣을 수 있는 후보가 정확히 이 체인이다.
        let el = start;
        while (el) {
            targets.add(el);
            el = el.parentElement;
        }
    }
    else {
        // 채팅 닫힘(사이드바 언마운트) 후에는 조상 체인이 소멸했으므로,
        // DOM identity 가 유지되어 stale 이 존속 가능한 컨테이너 고정 목록을 스윕한다.
        ['#room-container', '#room-container .body', '#room-container .layout', '#root'].forEach(sel => {
            const found = document.querySelector(sel);
            if (found)
                targets.add(found);
        });
    }
    // documentElement 는 P1(a) 후보(큰-뷰포트 슬랙 132px 실측) — 항상 포함.
    targets.add(document.body);
    targets.add(document.documentElement);
    targets.forEach(el => {
        // 이미 0 이면 DOM 쓰기 자체가 없다(완전 no-op) — 부작용 원천 차단.
        if (el.scrollTop === 0 && el.scrollLeft === 0)
            return;
        if (el === document.documentElement || el === document.body) {
            resetElementScroll(el, true, true);
            return;
        }
        // overflow 가 hidden 인 축만 리셋 — 의도적 스크롤러(auto/scroll)는 보존.
        const style = window.getComputedStyle(el);
        const resetTop = style.overflowY === 'hidden';
        const resetLeft = style.overflowX === 'hidden';
        if (!resetTop && !resetLeft)
            return;
        resetElementScroll(el, resetTop, resetLeft);
    });
    // window(visual viewport 기준) 잔여 오프셋도 개별 판정 후 원복.
    if (window.scrollY || window.scrollX) {
        try {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
        catch (e) {
            // scroll-behavior:smooth 를 타지 않도록 프로퍼티 직접 세팅으로 폴백
            document.documentElement.scrollTop = 0;
            document.documentElement.scrollLeft = 0;
        }
    }
};
